<template>
  <modal :show="show" modal-classes="modal-dialog-centered modal-xl" @close="$emit('update:show', false)">
    <template slot="header">
      <div class="generator-heading">
        <span class="generator-icon"><i class="fa fa-wand-magic-sparkles" aria-hidden="true"></i></span>
        <div>
          <h3 class="mb-1">Generate CBT questions</h3>
          <p class="mb-0">Create a draft, review every answer, then add approved questions to this exam.</p>
        </div>
      </div>
    </template>

    <section v-if="!questions.length" class="generator-form">
      <div class="generator-context">
        <div>
          <span class="context-label">Title</span>
          <strong v-html="header.examTitle"></strong>
        </div>       
        <small>Class and subject come from the selected CBT header so generated questions are saved to the matching question bank.</small>
      </div>

      <div class="form-group">
        <label for="ai-question-topics">Topics or subtopics <span class="text-danger" aria-hidden="true">*</span></label>
        <textarea id="ai-question-topics" ref="topicInput" v-model="topics" class="form-control" rows="3" maxlength="500"
          :class="{ 'is-invalid': topicError }" :aria-invalid="topicError ? 'true' : 'false'" required
          placeholder="For example: fractions, equivalent fractions, adding unlike denominators"></textarea>
        <div v-if="topicError" class="invalid-feedback">{{ topicError }}</div>
        <small class="form-text text-muted">Enter one or more topics, separated by commas or new lines.</small>
      </div>

      <div class="form-group count-field">
        <label for="ai-question-count">Number of questions</label>
        <input id="ai-question-count" v-model.number="questionCount" class="form-control" type="number" min="1" max="25" step="1">
        <small class="form-text text-muted">Choose between 1 and 25 questions per generation.</small>
      </div>

      <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
      <div v-if="loading" class="generation-progress" role="status" aria-live="polite">
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <span>Preparing questions for {{ header.subjectName || 'this subject' }}…</span>
      </div>
    </section>

    <section v-else class="generator-preview">
      <div class="preview-summary">
        <div>
          <h4 class="mb-1">Review generated questions</h4>
          <p class="mb-0">Edit the wording or answers before adding them to the question bank.</p>
        </div>
        <span class="badge badge-primary">{{ selectedCount }} / {{ questions.length }} selected</span>
      </div>

      <article v-for="(question, index) in questions" :key="question.localId" class="preview-question">
        <div class="preview-question-top">
          <label class="question-select">
            <input v-model="question.selected" type="checkbox">
            <span>Question {{ index + 1 }}</span>
          </label>
          <span v-if="isDuplicate(question)" class="badge badge-warning" title="Similar to a question already in this question bank">
            Possible duplicate
          </span>
        </div>

        <div class="form-group">
          <label :for="`generated-question-${question.localId}`">Question</label>
          <textarea :id="`generated-question-${question.localId}`" v-model.trim="question.examQuestion" class="form-control" rows="2"></textarea>
        </div>

        <div class="row">
          <div v-for="option in optionKeys" :key="option" class="col-md-6 form-group">
            <label :for="`generated-${option}-${question.localId}`" class="option-label">
              <input v-model="question.correctOption" type="radio" :name="`correct-${question.localId}`" :value="option">
              Option {{ option }} <span v-if="question.correctOption === option" class="correct-label">Correct answer</span>
            </label>
            <textarea :id="`generated-${option}-${question.localId}`" v-model.trim="question[`option${option}`]" class="form-control" rows="2"></textarea>
          </div>
        </div>

        <div class="form-group mb-0">
          <label :for="`generated-explanation-${question.localId}`">Answer explanation</label>
          <textarea :id="`generated-explanation-${question.localId}`" v-model.trim="question.answerDetails" class="form-control" rows="2"></textarea>
        </div>
        <p v-if="question.selected && validationMessage(question)" class="question-validation mb-0" role="alert">{{ validationMessage(question) }}</p>
      </article>

      <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
    </section>

    <template slot="footer">
      <base-button type="default" :disabled="loading" @click="close">Cancel</base-button>
      <base-button v-if="questions.length" type="secondary" :disabled="loading" @click="questions = []; error = ''">
        <i class="fa fa-rotate-left mr-1" aria-hidden="true"></i>Change criteria
      </base-button>
      <base-button v-if="!questions.length" type="primary" :disabled="loading || !canGenerate" @click="generate">
        <span v-if="loading" class="spinner-border spinner-border-sm mr-2" aria-hidden="true"></span>
        <i v-else class="fa fa-wand-magic-sparkles mr-1" aria-hidden="true"></i>
        {{ loading ? 'Generating…' : 'Generate questions' }}
      </base-button>
      <base-button v-else type="success" :disabled="!canSave" @click="saveSelected">
        <i class="fa fa-check mr-1" aria-hidden="true"></i>Add {{ selectedCount }} to question bank
      </base-button>
    </template>
  </modal>
</template>

<script>
const OPTION_KEYS = ['A', 'B', 'C', 'D'];

function plainText(value) {
  return String(value || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
}

function normalizeQuestion(item, index) {
  const correctOption = String(item.correctOption || item.CorrectOption || item.correctAnswer || item.CorrectAnswer || '').toUpperCase().replace(/[^A-D]/g, '').slice(0, 1);
  const mapped = {
    localId: `generated-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
    selected: true,
    examQuestion: plainText(item.examQuestion || item.ExamQuestion || item.question || item.Question),
    answerDetails: plainText(item.answerDetails || item.AnswerDetails || item.explanation || item.Explanation),
    optionA: plainText(item.optionA || item.OptionA || item.options && item.options.A),
    optionB: plainText(item.optionB || item.OptionB || item.options && item.options.B),
    optionC: plainText(item.optionC || item.OptionC || item.options && item.options.C),
    optionD: plainText(item.optionD || item.OptionD || item.options && item.options.D),
    correctOption
  };
  if (!mapped.correctOption) {
    const answerFlags = OPTION_KEYS.filter(option => item[`isOption${option}Answer`] === true || item[`IsOption${option}Answer`] === true);
    mapped.correctOption = answerFlags.length === 1 ? answerFlags[0] : '';
  }
  return mapped;
}

export default {
  name: 'CbtQuestionGenerator',
  props: {
    show: { type: Boolean, default: false },
    header: { type: Object, default: () => ({}) },
    existingQuestions: { type: Array, default: () => [] }
  },
  data() {
    return {
      topics: '',
      questionCount: 5,
      questions: [],
      loading: false,
      error: '',
      topicError: ''
    };
  },
  computed: {
    optionKeys() { return OPTION_KEYS; },
    selectedCount() { return this.questions.filter(question => question.selected).length; },
    canGenerate() {
      return Number.isInteger(Number(this.questionCount)) && Number(this.questionCount) >= 1 && Number(this.questionCount) <= 25 && !!this.header.questionHeaderId;
    },
    canSave() {
      const selected = this.questions.filter(question => question.selected);
      return selected.length > 0 && selected.every(question => !this.validationMessage(question));
    }
  },
  watch: {
    show(value) {
      if (value) {
        this.topics = '';
        this.questionCount = 5;
        this.questions = [];
        this.error = '';
        this.topicError = '';
      }
    }
  },
  methods: {
    close() {
      if (!this.loading) this.$emit('update:show', false);
    },
    normalized(value) {
      return plainText(value).toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    },
    isDuplicate(question) {
      const candidate = this.normalized(question.examQuestion);
      if (!candidate) return false;
      return this.existingQuestions.some(existing => this.normalized(existing.examQuestion || existing.ExamQuestion) === candidate);
    },
    validationMessage(question) {
      if (!question.examQuestion) return 'Enter a question before saving.';
      if (this.isDuplicate(question)) return 'This matches a question already in the bank. Edit the wording before adding it.';
      if (OPTION_KEYS.some(option => !question[`option${option}`])) return 'Complete all four answer options.';
      if (!OPTION_KEYS.includes(question.correctOption)) return 'Select exactly one correct answer.';
      if (!question.answerDetails) return 'Add an explanation for the correct answer.';
      return '';
    },
    async generate() {
      // Guard immediately, before Vue has a chance to render the disabled state.
      if (this.loading) return;
      if (!this.topics.trim()) {
        this.topicError = 'Enter at least one topic or subtopic before generating questions.';
        this.$nextTick(() => {
          if (this.$refs.topicInput) this.$refs.topicInput.focus();
        });
        return;
      }
      this.topicError = '';
      if (!this.canGenerate) {
        this.error = 'Choose a valid number of questions (1–25).';
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const response = await this.$store.dispatch('SchoolQuestion/GenerateAIQuestionsGeneral', {
          questionHeaderId: Number(this.header.questionHeaderId),
          topics: this.topics.trim(),
          numberOfQuestions: Number(this.questionCount),
          questionType: 'multiple-choice'
        });
        if (!response || response.responseCode && response.responseCode !== '00') {
          throw new Error(response && response.responseMessage || 'The question service could not generate questions.');
        }
        const result = response.data || response.questions || response;
        const items = Array.isArray(result) ? result : result && (result.questions || result.Questions);
        if (!Array.isArray(items) || !items.length) throw new Error('The question service returned no usable questions. Please try again.');
        if (items.length !== Number(this.questionCount)) {
          throw new Error(`The service returned ${items.length} question${items.length === 1 ? '' : 's'}, but ${this.questionCount} were requested. Nothing was saved; please try again.`);
        }
        const normalized = items.map(normalizeQuestion);
        if (normalized.some(question => !question.examQuestion || OPTION_KEYS.some(option => !question[`option${option}`]) || !OPTION_KEYS.includes(question.correctOption))) {
          throw new Error('The generated response was incomplete. Nothing was added. Please try generating again.');
        }
        const seen = new Set();
        this.questions = normalized.filter(question => {
          const key = this.normalized(question.examQuestion);
          if (seen.has(key)) return false;
          seen.add(key);
          if (this.isDuplicate(question)) question.selected = false;
          return true;
        });
        if (!this.questions.length) throw new Error('The response contained duplicate questions only. Please try again with different topics.');
      } catch (error) {
        this.error = error.response && error.response.data && (error.response.data.responseMessage || error.response.data.message)
          || error.message
          || 'Question generation failed. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    saveSelected() {
      if (!this.canSave) {
        this.error = 'Fix the validation messages for selected questions before saving.';
        return;
      }
      const selected = this.questions.filter(question => question.selected).map(question => ({
        ExamQuestion: question.examQuestion,
        AnswerDetails: question.answerDetails,
        OptionA: question.optionA,
        OptionB: question.optionB,
        OptionC: question.optionC,
        OptionD: question.optionD,
        IsOptionAAnswer: question.correctOption === 'A',
        IsOptionBAnswer: question.correctOption === 'B',
        IsOptionCAnswer: question.correctOption === 'C',
        IsOptionDAnswer: question.correctOption === 'D'
      }));
      this.$emit('save', selected);
    }
  }
};
</script>

<style scoped>
.generator-heading { display: flex; align-items: center; gap: 0.85rem; }
.generator-heading h3 { color: var(--app-text); font-size: 1.12rem; font-weight: 700; }
.generator-heading p, .preview-summary p { color: var(--app-muted); font-size: 0.86rem; }
.generator-icon { display: inline-flex; align-items: center; justify-content: center; width: 2.6rem; height: 2.6rem; flex: 0 0 2.6rem; border-radius: 0.8rem; background: rgba(53, 106, 230, 0.12); color: #356ae6; }
.generator-context { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.8rem; margin-bottom: 1.5rem; padding: 1rem; border: 1px solid var(--app-border); border-radius: 0.85rem; background: var(--app-surface-strong); }
.generator-context > div { display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
.generator-context strong { overflow: hidden; color: var(--app-text); text-overflow: ellipsis; white-space: nowrap; }
.context-label { color: var(--app-muted); font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.generator-context small { grid-column: 1 / -1; color: var(--app-muted); }
.count-field { max-width: 16rem; }
.generation-progress { display: flex; align-items: center; gap: 0.7rem; padding: 0.8rem 1rem; border-radius: 0.7rem; background: rgba(53, 106, 230, 0.08); color: var(--app-text); }
.preview-summary { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.preview-summary h4 { color: var(--app-text); font-size: 1rem; font-weight: 700; }
.preview-question { margin-bottom: 1rem; padding: 1rem; border: 1px solid var(--app-border); border-radius: 0.85rem; background: var(--app-surface); }
.preview-question-top { display: flex; align-items: center; justify-content: space-between; gap: 0.7rem; margin-bottom: 0.9rem; }
.question-select, .option-label { display: flex; align-items: center; gap: 0.5rem; color: var(--app-text); font-weight: 650; }
.question-select { margin: 0; }
.option-label { font-size: 0.85rem; }
.correct-label { color: #16834b; font-size: 0.72rem; font-weight: 600; }
.question-validation { margin-top: 0.55rem; color: #b42318; font-size: 0.82rem; }
@media (max-width: 575.98px) {
  .generator-context { grid-template-columns: 1fr; }
  .generator-context small { grid-column: auto; }
  .preview-summary { align-items: flex-start; flex-direction: column; }
}
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { scroll-behavior: auto !important; }
}
</style>
